// /app/frontend/src/lib/onboardingMilestones.js
//
// Lightweight one-shot toast helper for the onboarding progress narrative.
// Each milestone fires AT MOST ONCE per tenant per browser session — we
// don't want to spam returning users with "Your AI is learning…" every
// time they refresh. The state lives in localStorage keyed by company_id
// so it survives reloads and tab switches but resets when the tenant logs
// out or clears their site data.
//
// Usage:
//   import { fireMilestone, MILESTONES } from "../lib/onboardingMilestones";
//   fireMilestone("first_resource", MILESTONES.FIRST_RESOURCE);
//
// Toast copy is product-oriented (warm, business-language) per
// stakeholder direction — not technical/process-oriented.

import { toast } from "sonner";

export const MILESTONES = {
  FIRST_RESOURCE:      "Knowledge base started — your AI Employee is learning your business.",
  WEBSITE_IMPORTED:    "Website imported successfully — generating AI understanding.",
  QA_READY_FOR_REVIEW: "Q&A ready for review — approve responses to activate publicly.",
  READY_TO_GO_LIVE:    "Your AI Assistant is now ready to go live.",
};

function _companyId() {
  try {
    const u = JSON.parse(localStorage.getItem("ren_user") || "null");
    return (u && (u.company_id || u.companyId)) || "default";
  } catch (_) { return "default"; }
}

function _key(milestoneKey) {
  return `rudras:milestone:${_companyId()}:${milestoneKey}`;
}

/**
 * Fire a milestone toast exactly once per tenant per browser session.
 * @param {string} key        — stable identifier ("first_resource", etc.)
 * @param {string} message    — the toast body (use a MILESTONES constant).
 * @param {object} options    — { force: bool — re-fire even if already seen }
 * @returns {boolean}         — true if the toast was actually shown.
 */
export function fireMilestone(key, message, options = {}) {
  if (!key || !message) return false;
  const storageKey = _key(key);
  if (!options.force) {
    try {
      if (localStorage.getItem(storageKey)) return false;
    } catch (_) { /* private mode — fall through and show every time */ }
  }
  try { localStorage.setItem(storageKey, new Date().toISOString()); } catch (_) {}
  toast.success(message, { duration: 5500 });
  return true;
}

/** Test helper — reset every milestone for the current tenant (used by manual QA only). */
export function _resetMilestonesForCurrentTenant() {
  try {
    const prefix = `rudras:milestone:${_companyId()}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch (_) {}
}
